import { useEffect, useState, useMemo } from "react";
import moment from "moment";
import get from "lodash/get";
import submit from "submit-form";
import StatCard from "./components/StatCard";
import SubscribersTable from "./components/SubscribersTable";
import SubscriberDetails from "./components/SubscriberDetails";

const initialTooltipState = { show: false, x: 0, y: 0, content: "" };

// This component is used for the subscriber list as well
const MembershipList = (
  { csvString, allSubs, subscribers, productType = "membership" },
  { locals }
) => {
  const [all, setAll] = useState({ activeSubs: [], churnedSubs: [] });
  const [active, setActive] = useState({
    currentSubs: [],
    currentPage: null,
    totalPages: null,
  });
  const [churned, setChurned] = useState({
    currentSubs: [],
    currentPage: null,
    totalPages: null,
  });

  const [sortConfig, setSortConfigState] = useState({
    key: "customer_name",
    direction: "ascending",
  });

  const [tooltip, setTooltipState] = useState(initialTooltipState);
  const [newSubsTooltip, setNewSubsTooltip] = useState(initialTooltipState);
  const [churnedSubsTooltip, setChurnedSubsTooltip] =
    useState(initialTooltipState);
  const [search, setSearch] = useState("");
  const userStr = productType === "membership" ? "members" : "subscribers";

  useEffect(() => {
    const Chartist = require("chartist");
    const { activeSubs, churnedSubs } = splitActiveList(subscribers);
    const { chartData } = allSubs;

    const setTooltip = (props) => {
      const {
        content = "",
        y = 0,
        x = 0,
        show = false,
        setter = setTooltipState,
      } = props;
      setter({
        show,
        content,
        x,
        y,
      });
    };

    const options = {
      distributeSeries: true,
      showGrid: false,
      width: "188px",
      height: "64px",
      fullwidth: true,

      axisY: {
        showGrid: true,
        showLabel: false,
        offset: 0,
      },
      axisX: { showGrid: false, showLabel: false, offset: 0 },
    };

    const drawFunction = (data, series, labels, setter) => {
      if (data.type === "bar") {
        const value = parseFloat(data.element._node.getAttribute("ct:value"));
        const indexOfBar = Math.floor(
          (parseInt(data.element._node.getAttribute("x1")) - 54) / 28
        );
        const tooltipText = `${labels[indexOfBar]}: ${value} ${userStr}`;
        data.element.attr({
          style: "stroke-width: 10px; stroke: var(--information);",
          tooltip_text: tooltipText,
        });

        data.element._node.addEventListener("mouseenter", ({ target }) => {
          setTooltip({
            show: true,
            x: -1 + 28 * indexOfBar,
            y: parseInt(target.getAttribute("y2")) - 70,
            content: target.getAttribute("tooltip_text"),
            setter,
          });
        });

        data.element._node.addEventListener("mouseleave", () => {
          setTooltip({
            show: false,
            setter,
          });
        });
      }
    };

    const totalSubsChartObject = new Chartist.Bar(
      ".total-subs-chart",
      {
        series: chartData.totalSubscribersArray,
      },
      options
    ).on("draw", function (data) {
      drawFunction(
        data,
        chartData.totalSubscribersArray,
        chartData.monthsArray
      );
    });

    const newSubsChartObject = new Chartist.Bar(
      ".new-subs-chart",
      {
        series: chartData.newSubscribersArray,
      },
      options
    ).on("draw", function (data) {
      drawFunction(
        data,
        chartData.newSubscribersArray,
        chartData.monthsArray,
        setNewSubsTooltip
      );
    });

    const churnedSubsChartObject = new Chartist.Bar(
      ".churned-subs-chart",
      {
        series: chartData.churnedSubscribersArray,
      },
      options
    ).on("draw", function (data) {
      drawFunction(
        data,
        chartData.churnedSubscribersArray,
        chartData.monthsArray,
        setChurnedSubsTooltip
      );
    });

    setAll({ activeSubs, churnedSubs });
  }, []);

  useEffect(() => {
    const { activeSubs, churnedSubs } = splitActiveList(subscribers);
    setAll({ activeSubs, churnedSubs });
  }, [subscribers]);

  const setSortConfig = ({ target }, direction = "ascending") => {
    setSortConfigState({ key: target.value, direction });
  };

  const sortTable = (subscribers, sortConfig, search) => {
    let sortedSubscribers = [...subscribers];
    if (search) {
      sortedSubscribers = sortedSubscribers.filter((sub) => {
        return (
          String(sub.customer_name)
            .toLowerCase()
            .includes(search.toLowerCase().trim()) ||
          String(sub.customer_email)
            .toLowerCase()
            .includes(search.toLowerCase().trim()) ||
          String(sub.phoneNumber)
            .toLowerCase()
            .includes(search.toLowerCase().trim()) ||
          String(sub.plan.interval)
            .toLowerCase()
            .includes(search.toLowerCase().trim())
        );
      });
    }
    if (sortConfig !== null) {
      sortedSubscribers.sort((a, b) => {
        if (get(a, sortConfig.key) < get(b, sortConfig.key)) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (get(a, sortConfig.key) > get(b, sortConfig.key)) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortedSubscribers;
  };

  const onActivePageChanged = (data) => {
    const { activeSubs } = all;
    const { currentPage, totalPages, pageLimit } = data;
    const offset = (currentPage - 1) * pageLimit;
    const currentActiveSubs = activeSubs.slice(offset, offset + pageLimit);

    setActive({
      currentPage,
      currentSubs: currentActiveSubs,
      totalPages,
    });
  };

  const onChurnedPageChanged = (data) => {
    const { churnedSubs } = all;
    const { currentPage, totalPages, pageLimit } = data;
    const offset = (currentPage - 1) * pageLimit;
    const currentChurnedSubs = churnedSubs.slice(offset, offset + pageLimit);

    setChurned({ currentPage, currentSubs: currentChurnedSubs, totalPages });
  };

  const splitActiveList = (subscribers) => {
    const splitList = {
      activeSubs: subscribers.filter((sub) =>
        moment().isBefore(sub.current_period_end_date)
      ),
      churnedSubs: subscribers.filter((sub) => {
        const now = moment();
        const diff = now.diff(sub.current_period_end_date);
        return diff > 0;
      }),
    };
    return splitList;
  };

  const { query, subscriberInvoices, upcomingInvoice } = locals;

  const [currentSubs, type] = useMemo(() => {
    let currentSubs = active.currentSubs;
    let type = "active";
    if (query?.type === "churned") {
      type = "churned";
      currentSubs = churned.currentSubs;
    }
    return [sortTable(currentSubs, sortConfig, search), type];
  }, [active, churned, query, sortConfig, search]);

  const toggleDrawer = (e, customer = "", price = "") => {
    e.preventDefault();
    submit("/actions/refresh", {
      method: "GET",
      body: {
        customer,
        price,
      },
    });
  };

  const cancelSubscription = (e) => {
    e.preventDefault();
    if (
      confirm(
        `Are you sure you want to cancel the ${productType} of this user? They will lose access to the app`
      )
    ) {
      submit("/actions/user/cancel-subscriber", {
        method: "POST",
        body: {
          customer: query.customer,
          type: productType,
        },
      });
    }
  };

  return (
    <main className="subscribers-list">
      <header className="stat-cards">
        <StatCard
          title={`Total ${userStr}`}
          tooltipText={`This is the count of average active ${userStr} count for the given calendar month`}
          graphTooltip={tooltip}
          current={allSubs.totalSubscribers}
          previous={allSubs.chartData.totalSubscribersArray[0]}
          chartClass="total-subs-chart"
        />

        <StatCard
          title={`New ${userStr}`}
          tooltipText={`This is the count of new ${userStr} that joined during the calendar month`}
          graphTooltip={newSubsTooltip}
          current={allSubs.newSubscribers}
          previous={allSubs.chartData.newSubscribersArray[0]}
          chartClass="new-subs-chart"
        />

        <StatCard
          title={`Churned ${userStr}`}
          tooltipText={`This is the count of newly churned ${userStr} during the calendar month`}
          graphTooltip={churnedSubsTooltip}
          current={allSubs.churnedSubscribers}
          previous={allSubs.chartData.churnedSubscribersArray[0]}
          chartClass="churned-subs-chart"
        />
      </header>

      <SubscribersTable
        tableTitle={
          productType === "membership" ? "Member list" : "Subscriber list"
        }
        sortConfig={sortConfig}
        handleSort={setSortConfig}
        subscribers={currentSubs}
        type={type}
        allActiveSubs={all.activeSubs}
        allChurnedSubs={all.churnedSubs}
        onActivePageChanged={onActivePageChanged}
        onChurnedPageChanged={onChurnedPageChanged}
        csvString={csvString}
        nameString={productType === "membership" ? "Member" : "Subscriber"}
        onDetailsClick={toggleDrawer}
        search={search}
        setSearch={setSearch}
        productType={productType}
      />

      <SubscriberDetails
        subscriptions={subscribers}
        customer={query?.customer}
        toggleDrawer={toggleDrawer}
        handleCancel={cancelSubscription}
        type={productType}
        subscriberInvoices={subscriberInvoices}
        price={query?.price}
        upcomingInvoice={upcomingInvoice}
      />
    </main>
  );
};

export default MembershipList;
